/**
 * Utilidades del certificado de peritaje de Automás: formateo, enmascarado y el semáforo
 * (porcentaje → tono → etiqueta) que usa el diseño.
 */

/**
 * Valores que el backend usa como "vacío". Ojo: algunos campos llegan como el *texto*
 * "null" (por ejemplo `engine` en el peritaje de RNU052), no como null real.
 */
export const isEmpty = (v: any): boolean => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string") {
        const normalized = v.trim().toLowerCase();
        return normalized === "" || normalized === "none" || normalized === "null" || normalized === "undefined";
    }
    return false;
};

export const text = (v: any, fallback = ""): string => (isEmpty(v) ? fallback : String(v).trim());

/** Marcador de campo sin dato. El diseño deja el guion largo en los campos vacíos. */
export const DASH = "—";

/** Quita tildes y pasa a mayúsculas, para poder comparar contra los textos de la API. */
export const normalize = (v: any): string => text(v).toUpperCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** "VEHICULO EN ESTANDAR" → "Vehiculo en estandar". El diseño usa mayúscula solo en la inicial. */
export const sentenceCase = (v: any): string => {
    const value = text(v).toLowerCase();
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
};

/** "MAZDA CX-30 GRAND TOURING" → "Mazda Cx-30 Grand Touring". La API entrega todo en mayúsculas. */
export const titleCase = (v: any): string =>
    text(v)
        .toLowerCase()
        .replace(/(^|[\s/(-])([a-zá-ú])/g, (_match, prefix: string, letter: string) => prefix + letter.toUpperCase());

const currencyFormatter = new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
});

const toNumber = (v: any): number | null => {
    if (isEmpty(v)) return null;
    const n = typeof v === "number" ? v : Number(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : null;
};

/** "89000000.00" → "$ 89.000.000". */
export const fmtCurrency = (v: any, fallback = DASH): string => {
    const n = toNumber(v);
    if (n === null) return fallback;
    return currencyFormatter.format(n).replace("$", "$ ");
};

/** 45300 → "45.300". */
export const fmtNumber = (v: any, fallback = DASH): string => {
    const n = toNumber(v);
    if (n === null) return fallback;
    return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n);
};

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const parseDate = (v: any): { year: string; month: string; day: string } | null => {
    if (isEmpty(v)) return null;
    const raw = String(v);
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (match) return { year: match[1], month: match[2], day: match[3] };

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return {
        year: String(parsed.getFullYear()),
        month: String(parsed.getMonth() + 1).padStart(2, "0"),
        day: String(parsed.getDate()).padStart(2, "0"),
    };
};

/** "2026-08-25" → "25-Ago-2026". Formato de los detalles del vehículo. */
export const fmtDate = (v: any, fallback = DASH): string => {
    const parts = parseDate(v);
    if (!parts) return isEmpty(v) ? fallback : String(v);
    return `${Number(parts.day)}-${MONTHS[Number(parts.month) - 1] ?? parts.month}-${parts.year}`;
};

/** "2026-08-25" → "2026 / 08 / 25". Formato de la cabecera, bajo el número de acta. */
export const fmtDateSlash = (v: any, fallback = DASH): string => {
    const parts = parseDate(v);
    if (!parts) return fallback;
    return `${parts.year} / ${parts.month} / ${parts.day}`;
};

/**
 * Dato reservado: se confirma que el campo existe, sin revelar el valor ni su longitud.
 *
 * La cantidad de puntos es fija a propósito. Usar `value.length` delataría cuántos caracteres
 * tiene el dato, que ya es información útil para adivinarlo.
 */
export const oculto = (v: any): string => (isEmpty(v) ? DASH : "••••••");

/**
 * La placa solo revela su último carácter, igual que el peritaje de Colserauto
 * (`lastDigit` en ModalColserAutoPeritaje/index.tsx) y que la ficha del vehículo.
 */
export const maskPlate = (v: any): string => {
    const value = normalize(v).replace(/\s+/g, "");
    return value ? value.slice(-1) : DASH;
};

export type Asegurable = "si" | "no" | "desconocido";

/**
 * Interpreta `insurable`, que llega como booleano pero puede venir `null` o como texto.
 *
 * Se distingue "no asegurable" de "no reportado": son cosas distintas y hoy la API manda `null`
 * en todos los peritajes, así que confundirlas marcaría como no asegurable a todo el parque.
 */
export const asegurabilidad = (v: any): Asegurable => {
    if (v === true) return "si";
    if (v === false) return "no";
    if (isEmpty(v)) return "desconocido";
    const value = normalize(v);
    if (/^(SI|TRUE|1|ASEGURABLE)$/.test(value)) return "si";
    if (/^(NO|FALSE|0)$/.test(value)) return "no";
    return "desconocido";
};

export type Tono = "good" | "fair" | "bad" | "neutral";

/**
 * Semáforo del diseño (leyenda de la hoja 1: Excelente/Bueno · Aceptable · Atención).
 * Se deduce de los valores del Figma: 100% y 77% verdes, 67% ámbar, 37% y 20% rojos.
 */
export const toneFromPercent = (v: any): Tono => {
    const n = toNumber(v);
    if (n === null) return "neutral";
    if (n >= 70) return "good";
    if (n >= 50) return "fair";
    return "bad";
};

/** Etiqueta que acompaña al porcentaje: "100% EXCELENTE", "77% BUENO", "20% ATENCIÓN". */
export const labelFromPercent = (v: any): string => {
    const n = toNumber(v);
    if (n === null) return "SIN DATO";
    if (n >= 90) return "EXCELENTE";
    if (n >= 70) return "BUENO";
    if (n >= 50) return "ACEPTABLE";
    return "ATENCIÓN";
};

/** "67% ACEPTABLE". Devuelve solo la etiqueta cuando no hay porcentaje. */
export const fmtScore = (v: any): string => {
    const n = toNumber(v);
    if (n === null) return labelFromPercent(null);
    return `${Math.round(n)}% ${labelFromPercent(n)}`;
};

/**
 * Equivalencia estado → porcentaje.
 *
 * IMPORTANTE: hoy `get-car-automas` no envía un porcentaje por categoría, solo `estado`
 * ("BUENO", "REGULAR", "MALO"). Este mapa es una derivación nuestra, no una medición de
 * Automás. Cuando el backend empiece a mandar `inspection_groups[].percentage` o
 * `visual_inspection[].porcentaje`, ese valor manda y esta tabla deja de usarse.
 */
const STATE_SCORES: { match: RegExp; score: number }[] = [
    { match: /EXCELENTE|OPTIMO|NUEVO|ORIGINAL/, score: 100 },
    { match: /MALO|DEFICIENTE|NO CUMPLE|CAMBIO|AVERIA|FUGA|GOLPE|CHOQUE|FISURA|OXIDO/, score: 20 },
    { match: /REGULAR|ACEPTABLE|RAYON|RAYADO|LEVE|DESGASTE|REVISAR|REPINTAD|RETOQUE/, score: 67 },
    { match: /BUENO|CUMPLE|APRUEBA|\bSI\b|CONFORME/, score: 90 },
];

/** Porcentaje derivado de un estado textual. `null` cuando el estado no se reconoce. */
export const scoreFromState = (state: any): number | null => {
    const value = normalize(state);
    if (!value) return null;
    const found = STATE_SCORES.find((entry) => entry.match.test(value));
    return found ? found.score : null;
};

/** Promedio redondeado, ignorando los valores nulos. `null` si no queda ninguno. */
export const average = (values: (number | null | undefined)[]): number | null => {
    const usable = values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (!usable.length) return null;
    return Math.round(usable.reduce((sum, v) => sum + v, 0) / usable.length);
};

export { toNumber };
